import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  Observable,
  of,
  ReplaySubject,
  Subject,
  tap,
} from 'rxjs';

import { BaseService } from './base.service';
import { environment } from '../../environments/environment';
import { AdvanceSearchCriteria } from '../models/AdvanceSearchCriteria';
import { BaggedInventory, BaggedInvProduct } from '../models/features/product/BaggedInventory';
import { Product } from '../models/features/product/Product';
import { PagedResponse } from '../models/PagedResponse';
import { DsmRecord } from '../models/features/dsm-rsm/DsmRsm';
import { AvailableHybrid } from '../models/features/available-hybrids/AvailableHybrid';
import { ProductCompanyYear } from '../models/features/product/ProductCompanyYear';
import { Trait } from '../models/Trait';

@Injectable({
  providedIn: 'root',
})
export class ProductService extends BaseService<Product> {
  private reloadProductsSubject = new Subject<void>();
  public reloadProducts$: Observable<void>;

  constructor(http: HttpClient) {
    super(http, `${environment.apiUrl}/product/v1`);
    this.reloadProducts$ = this.reloadProductsSubject.asObservable();
  }

  public reloadProducts(): void {
    this.reloadProductsSubject.next();
  }
  public searchProducts(
    advanceSearchCriteria: AdvanceSearchCriteria,
    year: number,
  ): Observable<HttpResponse<PagedResponse<BaggedInventory>>> {
    this.loadingSubject.next(true);
    return this.httpClient
      .post<
        PagedResponse<BaggedInventory>
      >(`${environment.apiUrl}/inventory/v1/baggedInventory/search/${year}`, advanceSearchCriteria, { observe: 'response' })
      .pipe(
        tap((response) => {
          this.loadingSubject.next(false);
        }),
        catchError(() => {
          this.loadingSubject.next(false);
          return EMPTY;
        }),
      );
  }

  public lookupByCode(code: string, acceptOrders = true): Observable<Product[]> {
    const params = new HttpParams().set('code', code).set('acceptOrders', acceptOrders);
    const url = `${this.baseApiUrl}/autocomplete`;
    return this.httpClient.get<Product[]>(url, { params });
  }

  public baggedProductLookUp(code: string, year: number, cropCode?: string): Observable<Product[]> {
    if (typeof code !== 'string' || !code?.trim()) {
      return of([] as any);
    }
    return this.httpClient.get<Product[]>(
      `${this.baseApiUrl}/baggedProductLookUp?code=${code}&year=${year}&cropCode=${cropCode}`,
    );
  }

  public lookupByCodeAndYear(
    code: string,
    year: number,
    count: number = 5,
  ): Observable<HttpResponse<Array<BaggedInvProduct>>> {
    const params = new HttpParams().set('code', code).set('year', year).set('count', count);
    return this.httpClient.get<BaggedInvProduct[]>(
      `${environment.apiUrl}/inventory/v1/baggedInventory/lookupByCodeAndYear`,
      { params, observe: 'response' },
    );
  }

  public dsmLookup(code: string, year: number): Observable<HttpResponse<AvailableHybrid>> {
    const params = new HttpParams().set('code', code).set('year', year);
    return this.httpClient.get<AvailableHybrid>(`${environment.apiUrl}/product/v1/dsmlookup`, {
      params,
      observe: 'response',
    });
  }

  public productLookup(
    year: number,
    crop: string,
    conditionerCode: string,
  ): Observable<HttpResponse<ProductCompanyYear[]>> {
    const params = new HttpParams()
      .set('year', year)
      .set('cropCode', crop)
      .set('conditionerCode', conditionerCode ? conditionerCode : '');
    return this.httpClient.get<ProductCompanyYear[]>(
      `${environment.apiUrl}/product/v1/lookupByCodeAndYear`,
      { params, observe: 'response' },
    );
  }

  public traitLookup(year: number, cropId: number): Observable<HttpResponse<Trait[]>> {
    const params = new HttpParams().set('year', year).set('cropId', cropId);
    return this.httpClient.get<Trait[]>(`${environment.apiUrl}/product/v1/lookupTraitByCrop`, {
      params,
      observe: 'response',
    });
  }
}
